import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { startCheckout, openPortal } from "../lib/billing";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { CreditCard, Loader2 } from "lucide-react";

const STATE_LABEL = {
  trialing: "Prova gratuita",
  active: "Attivo",
  grace: "Pagamento non riuscito",
  readonly: "Sola lettura",
  pending: "In attesa di verifica email",
};

const UsageMeter = ({ label, u, testid }) => {
  const pct = Math.min(100, Math.round((u.used / u.limit) * 100));
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline gap-2">
        <span className="text-xs text-text-secondary">{label}</span>
        <span className={`text-xs font-medium ${pct >= 100 ? "text-red-600" : "text-text-primary"}`}>
          {u.used} di {u.limit}
        </span>
      </div>
      <div className="w-36 h-1.5 rounded-full bg-subtle mt-1 overflow-hidden">
        <div className={`h-full rounded-full ${pct >= 100 ? "bg-red-500" : "bg-accent"}`}
             style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export const BillingCard = () => {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/billing/subscription").then(({ data }) => setSub(data)).catch(() => {});
    api.get("/billing/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, [user]);

  if (!user || user.role !== "admin" || !sub || sub.state === "legacy") return null;

  const managed = sub.state === "active" || sub.state === "grace";
  const dateIso = sub.state === "trialing" ? sub.trial_ends_at : sub.current_period_end;
  const dateLabel = sub.state === "trialing" ? "Fine prova"
    : sub.cancel_at_period_end ? "Attivo fino al" : "Prossimo rinnovo";

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch { setBusy(false); }
  };

  return (
    <Card className="bg-surface border border-border rounded-xl shadow-sm p-8" data-testid="profile-billing-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-medium text-text-primary mb-1 flex items-center gap-2">
            <CreditCard size={18} className="text-accent" /> Abbonamento
          </h2>
          <p className="text-sm text-text-secondary" data-testid="billing-card-plan">
            Piano <strong>{sub.plan_name}</strong> (fatturazione annuale) · {STATE_LABEL[sub.state] || sub.state}
            {sub.state === "trialing" && ` — ${sub.days_left} ${sub.days_left === 1 ? "giorno rimasto" : "giorni rimasti"}`}
          </p>
          <p className="text-sm text-text-secondary mt-1">
            {sub.users_count}/{sub.max_users} utenti
            {dateIso ? ` · ${dateLabel}: ${new Date(dateIso).toLocaleDateString("it-IT")}` : ""}
          </p>
        </div>
        <div className="flex-shrink-0">
          {managed ? (
            <button type="button" className="wl-btn wl-btn--primary" disabled={busy}
                    onClick={() => run(openPortal)} data-testid="billing-manage-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : "Gestisci abbonamento"}
            </button>
          ) : (
            <button type="button" className="wl-btn wl-btn--primary" disabled={busy}
                    onClick={() => run(() => startCheckout(sub.plan, "yearly"))}
                    data-testid="billing-activate-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : "Attiva l'abbonamento"}
            </button>
          )}
        </div>
      </div>
      {(usage?.ai_ops || usage?.searches) && (
        <div className="flex gap-8 mt-4" data-testid="billing-usage">
          {usage.ai_ops && <UsageMeter label="Operazioni AI (mese)" u={usage.ai_ops} testid="usage-ai-ops" />}
          {usage.searches && <UsageMeter label="Ricerche (mese)" u={usage.searches} testid="usage-searches" />}
        </div>
      )}
      {managed && (
        <p className="text-xs text-text-muted mt-3">
          Fatture, carta, cambio piano e disdetta si gestiscono in sicurezza su Stripe.
        </p>
      )}
    </Card>
  );
};
